import { useState, useEffect, useCallback } from "react";
import {
  HiOutlineChatBubbleLeftRight, HiOutlineFlag, HiOutlinePlus, HiOutlineTrash,
  HiOutlineEyeSlash, HiOutlineEye, HiOutlineLockClosed, HiOutlineLockOpen,
  HiOutlineSparkles, HiOutlineXMark, HiOutlineCheckCircle,
  HiOutlineArrowTopRightOnSquare, HiOutlineArrowPath, HiOutlineLink,
  HiOutlineUserGroup, HiOutlineBolt, HiOutlineChatBubbleBottomCenterText,
  HiOutlineExclamationTriangle, HiOutlineKey, HiOutlineArchiveBox, HiOutlineChevronDown,
} from "react-icons/hi2";
import AdminLayout from "./AdminLayout";
import { boardAdminAPI, communityAPI } from "../services/api";
import { useCurrentAdmin, hasPermission } from "./permissions";
import toast from "react-hot-toast";
import "./AdminBoard.css";

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }) : "—";

function CategoryModal({ category, onClose, onSaved }) {
  const isEdit = !!category;
  const [name, setName] = useState(category?.name || "");
  const [description, setDescription] = useState(category?.description || "");
  const [alumniOnly, setAlumniOnly] = useState(!!category?.alumni_only);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!name.trim()) { setError("Name is required."); return; }
    setSaving(true); setError("");
    try {
      const payload = { name: name.trim(), description: description.trim() || null, alumni_only: alumniOnly };
      if (isEdit) await boardAdminAPI.updateCategory(category.id, payload);
      else        await boardAdminAPI.createCategory(payload);
      toast.success(isEdit ? "Category updated." : "Category created.");
      onSaved();
    } catch (err) { setError(err.message || "Failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="aap__contact-overlay" onClick={onClose}>
      <div className="aap__contact-modal" onClick={e => e.stopPropagation()} style={{maxWidth:"430px"}}>
        <div className="aap__contact-head">
          <h3>{isEdit ? "Edit Category" : "New Category"}</h3>
          <button type="button" onClick={onClose}><HiOutlineXMark/></button>
        </div>
        <div className="abd__field">
          <label>Name</label>
          <input className="arb__form-input" value={name} placeholder="e.g. Introductions"
            onChange={e => { setName(e.target.value); setError(""); }}/>
        </div>
        <div className="abd__field">
          <label>Description <span className="abd__optional">(optional)</span></label>
          <textarea className="arb__form-input" rows={2} value={description}
            placeholder="What belongs in this space?"
            onChange={e => setDescription(e.target.value)}/>
        </div>
        <label className="abd__check">
          <input type="checkbox" checked={alumniOnly} onChange={e => setAlumniOnly(e.target.checked)}/>
          <span>
            Alumni only
            <em>Only members who've completed a retreat can read or post here.</em>
          </span>
        </label>
        {error && <p className="abd__error">{error}</p>}
        <div className="abd__modal-actions">
          <button type="button" className="a-btn a-btn--outline" onClick={onClose}>Cancel</button>
          <button type="button" className="a-btn a-btn--rust" disabled={saving} onClick={save}>
            {saving ? "Saving…" : isEdit ? "Save" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Post detail — read the full post + replies before acting on them ── */
function PostDetailModal({ postId, onClose, onChanged, canManage }) {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchPost = useCallback(() => {
    setLoading(true);
    boardAdminAPI.getPost(postId)
      .then(res => setPost(res?.data || null))
      .catch(err => toast.error(err.message || "Couldn't load that post."))
      .finally(() => setLoading(false));
  }, [postId]);

  useEffect(() => { fetchPost(); }, [fetchPost]);

  const act = async (action) => {
    setBusy(true);
    try {
      await boardAdminAPI.moderatePost(postId, action);
      toast.success("Updated.");
      fetchPost(); onChanged();
    } catch (err) { toast.error(err.message || "Failed."); }
    finally { setBusy(false); }
  };

  const actReply = async (replyId, action) => {
    setBusy(true);
    try {
      await boardAdminAPI.moderateReply(replyId, action);
      toast.success("Updated.");
      fetchPost(); onChanged();
    } catch (err) { toast.error(err.message || "Failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="aap__contact-overlay" onClick={onClose}>
      <div className="aap__contact-modal abd__detail" onClick={e=>e.stopPropagation()}>
        <div className="aap__contact-head">
          <h3>Post Detail</h3>
          <button type="button" onClick={onClose}><HiOutlineXMark/></button>
        </div>

        {loading ? (
          <div className="arb__loading"><div className="acr__spinner"/></div>
        ) : !post ? (
          <p className="aap__placeholder">Post not found.</p>
        ) : (
          <>
            <div className="abd__detail-head">
              <p className="abd__detail-title">
                {post.title}
                {post.status === "hidden" && <span className="abd__badge abd__badge--hidden">Hidden</span>}
                {!!post.is_pinned && <span className="abd__badge">Pinned</span>}
                {!!post.is_locked && <span className="abd__badge">Locked</span>}
              </p>
              <p className="abd__detail-meta">
                {post.category_name} · {post.author_name} · {fmt(post.created_at)}
              </p>
            </div>

            <div className="abd__detail-body">{post.body}</div>

            {canManage && (
              <div className="abd__detail-actions">
                <button type="button" className="a-btn a-btn--outline" disabled={busy}
                  onClick={()=>act(post.is_pinned ? "unpin" : "pin")}>
                  <HiOutlineSparkles/> {post.is_pinned ? "Unpin" : "Pin"}
                </button>
                <button type="button" className="a-btn a-btn--outline" disabled={busy}
                  onClick={()=>act(post.is_locked ? "unlock" : "lock")}>
                  {post.is_locked ? <HiOutlineLockOpen/> : <HiOutlineLockClosed/>}
                  {post.is_locked ? "Unlock" : "Lock"}
                </button>
                <button type="button" className="a-btn a-btn--outline" disabled={busy}
                  onClick={()=>act(post.status === "hidden" ? "restore" : "hide")}>
                  {post.status === "hidden" ? <HiOutlineEye/> : <HiOutlineEyeSlash/>}
                  {post.status === "hidden" ? "Restore" : "Hide"}
                </button>
              </div>
            )}

            <p className="abd__detail-replies-title">
              {post.replies.length} {post.replies.length === 1 ? "Reply" : "Replies"}
            </p>

            {post.replies.length === 0 ? (
              <p className="abd__detail-noreplies">No replies yet.</p>
            ) : (
              <div className="abd__detail-replies">
                {post.replies.map(r => (
                  <div key={r.id} className={`abd__detail-reply ${r.status !== "published" ? "abd__detail-reply--hidden" : ""}`}>
                    <div className="abd__detail-reply-head">
                      <span className="abd__detail-reply-author">{r.author_name}</span>
                      <span className="abd__detail-reply-time">{fmt(r.created_at)}</span>
                      {r.status !== "published" && (
                        <span className="abd__badge abd__badge--hidden">{r.status}</span>
                      )}
                      {canManage && r.status !== "removed" && (
                        <button type="button" className="abd__detail-reply-btn" disabled={busy}
                          title={r.status === "hidden" ? "Restore reply" : "Hide reply"}
                          onClick={()=>actReply(r.id, r.status === "hidden" ? "restore" : "hide")}>
                          {r.status === "hidden" ? <HiOutlineEye/> : <HiOutlineEyeSlash/>}
                        </button>
                      )}
                    </div>
                    <p className="abd__detail-reply-body">{r.body}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ── Discourse connection status ──
   The member community runs on a self-hosted Discourse; Forge signs members
   in and assigns their groups. This card shows whether it's connected and
   exactly what to configure on the Discourse side. */
function CommunityStatus({ st, canManage, onSynced }) {
  const [syncing, setSyncing] = useState(false);
  const [makingGroups, setMakingGroups] = useState(false);

  const setUpGroups = async () => {
    setMakingGroups(true);
    try { const r = await communityAPI.setUpGroups(); toast.success(r?.message || "Done."); onSynced?.(); }
    catch (err) { toast.error(err.message || "Couldn't set up the groups."); }
    finally { setMakingGroups(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try { const r = await communityAPI.syncAll(); toast.success(r?.message || "Synced."); onSynced?.(); }
    catch (err) { toast.error(err.message || "Sync failed."); }
    finally { setSyncing(false); }
  };

  return (
    <div className={`acard abd__community ${st.enabled ? "is-on" : ""}`}>
      <div className="abd__community-head">
        <span className={`abd__community-dot ${st.enabled ? "is-on" : ""}`} />
        <div>
          <p className="abd__community-title">
            {st.enabled ? "Community forum is connected" : "Community forum isn't connected yet"}
          </p>
          <p className="abd__community-sub">
            {st.enabled
              ? <>Forge members and Makua guests share <b>{st.url.replace(/^https?:\/\//, "")}</b>. Everyone signs in through Forge (Makua guests with their Makua sign-in). Groups follow each person's standing at Forge and their stays at Makua automatically{!st.makuaLinked && <> (Makua's groups start once <code>MAKUA_API_URL</code> is set)</>}. Moderation happens in Discourse.</>
              : <>Until Discourse is connected, members use the built-in board below. To connect, set <code>DISCOURSE_URL</code> and <code>DISCOURSE_CONNECT_SECRET</code> on the server.</>}
          </p>
        </div>
        {st.enabled && (
          <div className="abd__community-actions">
            <a href={`${st.url}/admin`} target="_blank" rel="noreferrer" className="a-btn a-btn--outline">Discourse admin <HiOutlineArrowTopRightOnSquare /></a>
            {canManage && st.instantSync && (
              <button type="button" className="a-btn a-btn--outline" onClick={setUpGroups} disabled={makingGroups}><HiOutlineUserGroup /> {makingGroups ? "Setting up…" : "Set up groups"}</button>
            )}
            {canManage && st.instantSync && (
              <button type="button" className="a-btn a-btn--rust" onClick={sync} disabled={syncing}><HiOutlineArrowPath /> {syncing ? "Syncing…" : "Sync groups now"}</button>
            )}
          </div>
        )}
      </div>
      <details className="abd__community-setup">
        <summary>{st.enabled ? "Connection details" : "Discourse settings to use"}</summary>
        <ul>
          <li><b>enable discourse connect</b>: on</li>
          <li><b>discourse connect url</b>: <code><HiOutlineLink /> {st.connectUrl}</code></li>
          <li><b>discourse connect secret</b>: same value as <code>DISCOURSE_CONNECT_SECRET</code></li>
          <li><b>discourse connect overrides email / name / username</b>: on</li>
          <li>{st.instantSync ? <>"Set up groups" makes any of these the forum is missing (private: only their members and staff see who's in them). Then give each category access to the right group:</> : <>Create these groups, then give each category access to the right group:</>}
            <div className="abd__groups">
              {st.groups.map((g) => <span key={g.name} title={g.description}><code>{g.name}</code> {g.description}</span>)}
            </div>
          </li>
          {!st.instantSync && <li>Optional: set <code>DISCOURSE_API_KEY</code> so group changes apply instantly (otherwise at each member's next forum sign-in).</li>}
        </ul>
      </details>
    </div>
  );
}

const num = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US"));
const ago = (d) => {
  if (!d) return "";
  const s = Math.max(0, (Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return fmt(d);
};

/* ── Live numbers from Discourse ──
   What members actually see and do. Read through the backend (which holds
   the API key) and cached there for a minute; "Refresh" skips the cache. */
function ForumOverview({ refreshKey }) {
  const [ov, setOv] = useState(null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback((fresh) => {
    setRefreshing(!!fresh);
    return communityAPI.overview(fresh)
      .then((r) => { setOv(r?.data || null); setError(""); })
      .catch((err) => setError(err.message || "Couldn't reach the forum."))
      .finally(() => setRefreshing(false));
  }, []);

  useEffect(() => { load(refreshKey > 0); }, [load, refreshKey]);

  if (!ov && !error) return <div className="acard abd__forum-loading"><div className="acr__spinner" /></div>;

  if (error && !ov) {
    return (
      <div className="acard abd__forum-note abd__forum-note--error">
        <HiOutlineExclamationTriangle />
        <div>
          <p><b>Couldn't load forum numbers.</b> {error}</p>
          <p>Members can still use the forum. This only affects the numbers on this page.</p>
        </div>
        <button type="button" className="a-btn a-btn--outline" onClick={() => load(true)} disabled={refreshing}>
          <HiOutlineArrowPath /> Try again
        </button>
      </div>
    );
  }

  if (ov.needsApiKey) {
    return (
      <div className="acard abd__forum-note">
        <HiOutlineKey />
        <div>
          <p><b>Add an API key to see live forum numbers here.</b></p>
          <p>Set <code>DISCOURSE_API_KEY</code> on the Forge backend and redeploy. The installer printed one, and it's also in <code>/root/forge-discourse.env</code>.</p>
        </div>
      </div>
    );
  }

  const { stats, groups, categories, recent, links } = ov;
  const maxGroup = Math.max(1, ...groups.map((g) => g.members || 0));
  const tiles = [
    { k: "members", label: "Members", value: stats.members, icon: <HiOutlineUserGroup />, tone: "rust", href: links.users },
    { k: "active", label: "Active this week", value: stats.activeWeek, icon: <HiOutlineBolt />, tone: "green" },
    { k: "topics", label: "Topics", value: stats.topics, icon: <HiOutlineChatBubbleLeftRight />, tone: "ink",
      sub: stats.topicsWeek ? `+${num(stats.topicsWeek)} this week` : null },
    { k: "posts", label: "Posts", value: stats.posts, icon: <HiOutlineChatBubbleBottomCenterText />, tone: "ink" },
    { k: "review", label: "Needs review", value: stats.needsReview, icon: <HiOutlineFlag />,
      tone: stats.needsReview ? "red" : "muted", href: links.review,
      sub: stats.needsReview ? "Open review queue" : "All clear" },
  ];

  return (
    <section className="abd__forum">
      <div className="abd__forum-bar">
        <p className="abd__forum-live"><span className="abd__live-dot" /> Live from the forum · updated {ago(ov.fetchedAt)}</p>
        <button type="button" className="a-btn a-btn--ghost" onClick={() => load(true)} disabled={refreshing}>
          <HiOutlineArrowPath className={refreshing ? "abd__spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="abd__kpis">
        {tiles.map((t) => {
          const body = (
            <>
              <span className={`abd__kpi-icon abd__kpi-icon--${t.tone}`}>{t.icon}</span>
              <span className="abd__kpi-label">{t.label}</span>
              <span className="abd__kpi-value">{num(t.value)}</span>
              {t.sub && <span className={`abd__kpi-sub ${t.tone === "red" ? "is-alert" : ""}`}>{t.sub}</span>}
            </>
          );
          return t.href
            ? <a key={t.k} href={t.href} target="_blank" rel="noreferrer" className={`acard abd__kpi abd__kpi--link ${t.tone === "red" ? "is-alert" : ""}`}>{body}</a>
            : <div key={t.k} className="acard abd__kpi">{body}</div>;
        })}
      </div>

      <div className="abd__forum-grid">
        <div className="acard abd__panel">
          <div className="abd__panel-head">
            <h3>Who can see what</h3>
            <p>Forum groups, set from each person's standing at Forge and their stays at Makua. Alumni keep their group.</p>
          </div>
          <ul className="abd__groups-list">
            {groups.map((g) => (
              <li key={g.key}>
                <a href={g.url} target="_blank" rel="noreferrer" className="abd__group">
                  <span className="abd__group-top">
                    <span className="abd__group-name">{g.label}</span>
                    <span className="abd__group-count">{num(g.members)}</span>
                  </span>
                  <span className="abd__group-track">
                    <span className="abd__group-fill" style={{ width: `${Math.max(2, ((g.members || 0) / maxGroup) * 100)}%` }} />
                  </span>
                  <span className="abd__group-desc">{g.description}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="acard abd__panel">
          <div className="abd__panel-head">
            <h3>Spaces</h3>
            <p>Forum categories. Each one opens to the group shown in Discourse.</p>
          </div>
          <ul className="abd__spaces">
            {categories.map((c) => (
              <li key={c.id}>
                <a href={c.url} target="_blank" rel="noreferrer" className="abd__space">
                  <span className="abd__space-swatch" style={{ background: c.color }} />
                  <span className="abd__space-main">
                    <span className="abd__space-name">
                      {c.name}
                      {c.restricted && <HiOutlineLockClosed title="Limited to a group" />}
                    </span>
                    <span className="abd__space-meta">
                      {c.subcategories?.length ? c.subcategories.join(" · ") : "No subforums"}
                    </span>
                  </span>
                  <span className="abd__space-stat"><b>{num(c.topics)}</b> topics</span>
                  <span className="abd__space-stat"><b>{num(c.posts)}</b> posts</span>
                </a>
              </li>
            ))}
          </ul>
          <a href={links.categories} target="_blank" rel="noreferrer" className="abd__panel-link">
            Manage spaces in Discourse <HiOutlineArrowTopRightOnSquare />
          </a>
        </div>
      </div>

      <div className="acard abd__panel">
        <div className="abd__panel-head abd__panel-head--row">
          <div>
            <h3>Latest conversations</h3>
            <p>Newest topics across every space.</p>
          </div>
          <a href={ov.url} target="_blank" rel="noreferrer" className="a-btn a-btn--outline">
            Open forum <HiOutlineArrowTopRightOnSquare />
          </a>
        </div>
        {recent.length === 0 ? (
          <p className="abd__empty">No topics yet. The first introductions will show up here.</p>
        ) : (
          <ul className="abd__topics">
            {recent.map((t) => (
              <li key={t.id}>
                <a href={t.url} target="_blank" rel="noreferrer" className="abd__topic">
                  <span className="abd__topic-title">{t.title}</span>
                  <span className="abd__topic-meta">
                    {t.category && (
                      <span className="abd__chip"><i style={{ background: t.color || "#6b6258" }} />{t.category}</span>
                    )}
                    <span>{t.replies} {t.replies === 1 ? "reply" : "replies"}</span>
                    <span>{num(t.views)} views</span>
                    <span>{ago(t.lastPostedAt || t.createdAt)}</span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/* ── Retreat spaces ──
   One private group and space per retreat date, for Forge's retreats and
   Makua's. Everyone on that retreat is put in its group automatically. */
const range = (a, b) => {
  const f = (d, y) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", ...(y ? { year: "numeric" } : {}) });
  return `${f(a)} – ${f(b, true)}`;
};

function RetreatSpaces({ canManage, instantSync, refreshKey, onOpened }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [opening, setOpening] = useState("");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    communityAPI.spaces().then((r) => { setData(r?.data || null); setError(""); }).catch((err) => setError(err.message || "Couldn't load retreats."));
  }, [refreshKey]);

  const open = async (r) => {
    const k = `${r.product}${r.id}`;
    setOpening(k);
    try {
      const res = await communityAPI.openSpace(r.product, r.id);
      toast.success(res?.message || "Space opened.");
      const fresh = await communityAPI.spaces();
      setData(fresh?.data || null);
      onOpened?.();
    } catch (err) { toast.error(err.message || "Couldn't open the space."); }
    finally { setOpening(""); }
  };

  // Dates nobody is on yet (and that have no space) are hidden until asked for.
  const shown = (rows) => (showAll ? rows : rows.filter((r) => r.people > 0 || r.space));
  const hidden = data ? [...data.forge, ...data.makua].length - [...shown(data.forge), ...shown(data.makua)].length : 0;

  const column = (title, all, empty) => {
    const rows = shown(all);
    return (
    <div className="abd__retreats-col">
      <h4>{title}</h4>
      {rows.length === 0 ? <p className="abd__empty">{all.length ? "Nobody is booked on these dates yet." : empty}</p> : (
        <ul className="abd__retreats">
          {rows.map((r) => (
            <li key={`${r.product}${r.id}`} className="abd__retreat">
              <span className="abd__retreat-main">
                <span className="abd__retreat-name">{r.name}</span>
                <span className="abd__retreat-meta">{range(r.start, r.end)} · {r.people} {r.product === "makua" ? (r.people === 1 ? "guest" : "guests") : (r.people === 1 ? "attendee" : "attendees")}</span>
              </span>
              {r.space ? (
                <a href={r.space.url} target="_blank" rel="noreferrer" className="abd__retreat-open">
                  <HiOutlineLockClosed /> Open in forum <HiOutlineArrowTopRightOnSquare />
                </a>
              ) : canManage && instantSync ? (
                <button type="button" className="a-btn a-btn--outline" onClick={() => open(r)} disabled={!!opening}>
                  <HiOutlinePlus /> {opening === `${r.product}${r.id}` ? "Opening…" : "Open a space"}
                </button>
              ) : <span className="abd__retreat-none">No space</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
    );
  };

  return (
    <div className="acard abd__panel">
      <div className="abd__panel-head">
        <h3>Retreat spaces</h3>
        <p>A private group and space for the people on one retreat, to meet before and keep in touch after. Everyone on it is added automatically, and taken out if their place is cancelled.{!instantSync && " Add DISCOURSE_API_KEY to open spaces from here."}</p>
      </div>
      {error ? <p className="abd__empty">{error}</p> : !data ? <div className="acr__spinner" /> : (
        <div className="abd__retreats-grid">
          {column("Forge", data.forge, "No Forge retreats from the last year.")}
          {data.makuaLinked
            ? column("Makua", data.makua, data.makuaError ? `Makua couldn't be reached: ${data.makuaError}` : "No Makua dates from the last year.")
            : <div className="abd__retreats-col"><h4>Makua</h4><p className="abd__empty">Set MAKUA_API_URL on the Forge backend to include Makua's dates.</p></div>}
        </div>
      )}
      {data && (hidden > 0 || showAll) && (
        <button type="button" className="abd__retreats-more" onClick={() => setShowAll((v) => !v)}>
          {showAll ? "Show only retreats with people on them" : `Show ${hidden} more date${hidden !== 1 ? "s" : ""} nobody is booked on yet`}
        </button>
      )}
    </div>
  );
}

/* ── The built-in board ──
   Used by members only while Discourse isn't connected. Once it is, this is
   kept as a backup (hidden from members) so nothing already posted is lost. */
function LegacyBoard({ canManage }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("reports");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openPost, setOpenPost] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    boardAdminAPI.overview()
      .then(res => setData(res?.data || null))
      .catch(err => toast.error(err.message || "Failed to load board."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const moderate = async (id, action) => {
    setBusy(true);
    try { await boardAdminAPI.moderatePost(id, action); toast.success("Updated."); fetchData(); }
    catch (err) { toast.error(err.message || "Failed."); }
    finally { setBusy(false); }
  };

  const resolveReport = async (id, status) => {
    setBusy(true);
    try { await boardAdminAPI.resolveReport(id, status); toast.success("Resolved."); fetchData(); }
    catch (err) { toast.error(err.message || "Failed."); }
    finally { setBusy(false); }
  };

  const deleteCategory = (cat) => {
    toast((t) => (
      <div style={{display:"flex",flexDirection:"column",gap:"0.5rem"}}>
        <p style={{margin:0,fontWeight:700}}>Delete {cat.name}?</p>
        <p style={{margin:0,fontSize:"0.78rem",color:"#6b6258"}}>
          If it has posts it'll be hidden instead, so nobody's conversations are destroyed.
        </p>
        <div style={{display:"flex",gap:"0.5rem"}}>
          <button onClick={async () => {
            toast.dismiss(t.id); setBusy(true);
            try { const r = await boardAdminAPI.removeCategory(cat.id); toast.success(r?.message || "Done."); fetchData(); }
            catch (err) { toast.error(err.message || "Failed."); }
            finally { setBusy(false); }
          }} style={{background:"#dc2626",color:"#fff",border:"none",borderRadius:"5px",padding:"0.35rem 0.75rem",cursor:"pointer",fontWeight:700,fontSize:"0.78rem"}}>Delete</button>
          <button onClick={() => toast.dismiss(t.id)}
            style={{background:"#f3f4f0",border:"none",borderRadius:"5px",padding:"0.35rem 0.75rem",cursor:"pointer",fontSize:"0.78rem"}}>Cancel</button>
        </div>
      </div>
    ), { duration: Infinity, style:{maxWidth:"330px"} });
  };

  if (loading && !data) return <div className="arb__loading"><div className="acr__spinner"/></div>;

  const reports = data?.reports || [];
  const posts = data?.posts || [];
  const categories = data?.categories || [];

  return (
    <>
      <div className="aap__stats-row" style={{gridTemplateColumns:"repeat(3,1fr)"}}>
        <div className="acard aap__stat-card">
          <div className="aap__stat-top">
            <div className="aap__stat-icon" style={{background:"rgba(220,38,38,0.1)"}}>
              <HiOutlineFlag style={{color:"#dc2626",fontSize:"1.1rem"}}/>
            </div>
            <p className="aap__stat-label">Open Reports</p>
          </div>
          <p className="aap__stat-value">{reports.length}</p>
        </div>
        <div className="acard aap__stat-card">
          <div className="aap__stat-top">
            <div className="aap__stat-icon" style={{background:"rgba(var(--admin-rust-rgb), 0.1)"}}>
              <HiOutlineChatBubbleLeftRight style={{color:"#e05a2b",fontSize:"1.1rem"}}/>
            </div>
            <p className="aap__stat-label">Posts</p>
          </div>
          <p className="aap__stat-value">{posts.filter(p => p.status === "published").length}</p>
        </div>
        <div className="acard aap__stat-card">
          <div className="aap__stat-top">
            <div className="aap__stat-icon" style={{background:"rgba(107,98,88,0.1)"}}>
              <HiOutlineSparkles style={{color:"#6b6258",fontSize:"1.1rem"}}/>
            </div>
            <p className="aap__stat-label">Categories</p>
          </div>
          <p className="aap__stat-value">{categories.length}</p>
        </div>
      </div>

      <div className="abd__tabs">
        {[
          { k:"reports",    label:`Reports (${reports.length})` },
          { k:"posts",      label:"All Posts" },
          { k:"categories", label:"Categories" },
        ].map(t => (
          <button key={t.k} type="button"
            className={`abd__tab ${tab === t.k ? "abd__tab--active" : ""}`}
            onClick={() => setTab(t.k)}>{t.label}</button>
        ))}
      </div>

      {tab === "reports" && (
        <div className="acard aq__section">
          {reports.length === 0 ? (
            <p className="aap__placeholder" style={{padding:"2rem",textAlign:"center"}}>
              No open reports. Nothing needs your attention right now.
            </p>
          ) : reports.map(r => (
            <div key={r.id} className="abd__row">
              <div className="abd__row-main">
                <p className="abd__row-title">
                  {r.post_title ? `Post: ${r.post_title}` : "Reply flagged"}
                </p>
                {r.reply_body && <p className="abd__row-excerpt">{r.reply_body.slice(0,160)}</p>}
                <p className="abd__row-meta">
                  Reported by {r.reporter_name} · {fmt(r.created_at)}
                  {r.reason ? ` · "${r.reason}"` : ""}
                </p>
              </div>
              {canManage && (
                <div className="abd__row-actions">
                  {r.post_id && (
                    <>
                      <button type="button" className="a-btn a-btn--outline" disabled={busy}
                        onClick={() => setOpenPost(r.post_id)}>
                        <HiOutlineEye/> View
                      </button>
                      <button type="button" className="a-btn a-btn--outline" disabled={busy}
                        onClick={() => moderate(r.post_id, "hide")}>
                        <HiOutlineEyeSlash/> Hide Post
                      </button>
                    </>
                  )}
                  <button type="button" className="a-btn a-btn--ghost" disabled={busy}
                    onClick={() => resolveReport(r.id, "dismissed")}>Dismiss</button>
                  <button type="button" className="a-btn a-btn--rust" disabled={busy}
                    onClick={() => resolveReport(r.id, "reviewed")}>
                    <HiOutlineCheckCircle/> Resolve
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "posts" && (
        <div className="acard aq__section">
          {posts.length === 0 ? (
            <p className="aap__placeholder" style={{padding:"2rem",textAlign:"center"}}>No posts yet.</p>
          ) : posts.map(p => (
            <div key={p.id} className="abd__row">
              <div className="abd__row-main abd__row-main--click" onClick={()=>setOpenPost(p.id)}>
                <p className="abd__row-title">
                  {p.is_pinned && <HiOutlineSparkles style={{color:"#e05a2b",verticalAlign:"-2px",marginRight:"0.25rem"}}/>}
                  {p.title}
                  {p.status === "hidden" && <span className="abd__badge abd__badge--hidden">Hidden</span>}
                  {p.is_locked && <span className="abd__badge">Locked</span>}
                </p>
                {p.body && <p className="abd__row-excerpt">{p.body.slice(0,150)}{p.body.length>150?"…":""}</p>}
                <p className="abd__row-meta">
                  {p.category_name} · {p.author_name} · {fmt(p.created_at)} · {p.reply_count} {p.reply_count === 1 ? "reply" : "replies"}
                </p>
              </div>
              {canManage && (
                <div className="abd__row-actions">
                  <button type="button" className="a-btn a-btn--ghost"
                    title="View post" onClick={()=>setOpenPost(p.id)}>
                    <HiOutlineEye/>
                  </button>
                  <button type="button" className="a-btn a-btn--ghost" disabled={busy}
                    title={p.is_pinned ? "Unpin" : "Pin"}
                    onClick={() => moderate(p.id, p.is_pinned ? "unpin" : "pin")}>
                    <HiOutlineSparkles/>
                  </button>
                  <button type="button" className="a-btn a-btn--ghost" disabled={busy}
                    title={p.is_locked ? "Unlock" : "Lock"}
                    onClick={() => moderate(p.id, p.is_locked ? "unlock" : "lock")}>
                    {p.is_locked ? <HiOutlineLockOpen/> : <HiOutlineLockClosed/>}
                  </button>
                  <button type="button" className="a-btn a-btn--ghost" disabled={busy}
                    title={p.status === "hidden" ? "Restore" : "Hide"}
                    onClick={() => moderate(p.id, p.status === "hidden" ? "restore" : "hide")}>
                    {p.status === "hidden" ? <HiOutlineEye/> : <HiOutlineEyeSlash/>}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "categories" && (
        <>
          {canManage && (
            <div style={{display:"flex",justifyContent:"flex-end",marginBottom:"0.75rem"}}>
              <button type="button" className="a-btn a-btn--rust" onClick={() => setEditing("new")}>
                <HiOutlinePlus/> New Category
              </button>
            </div>
          )}
          <div className="acard aq__section">
            {categories.map(c => (
              <div key={c.id} className="abd__row">
                <div className="abd__row-main">
                  <p className="abd__row-title">
                    {c.name}
                    {!!c.alumni_only && <span className="abd__badge abd__badge--alumni">Alumni only</span>}
                    {!c.active && <span className="abd__badge abd__badge--hidden">Hidden</span>}
                  </p>
                  {c.description && <p className="abd__row-excerpt">{c.description}</p>}
                  <p className="abd__row-meta">{c.post_count} {Number(c.post_count) === 1 ? "post" : "posts"}</p>
                </div>
                {canManage && (
                  <div className="abd__row-actions">
                    <button type="button" className="a-btn a-btn--ghost" onClick={() => setEditing(c)}>Edit</button>
                    <button type="button" className="a-btn a-btn--ghost" style={{color:"#dc2626"}}
                      onClick={() => deleteCategory(c)}><HiOutlineTrash/></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {openPost && (
        <PostDetailModal postId={openPost} canManage={canManage}
          onClose={()=>setOpenPost(null)} onChanged={fetchData}/>
      )}

      {editing && (
        <CategoryModal category={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchData(); }}/>
      )}
    </>
  );
}

export default function AdminBoard() {
  const { admin, loading: permLoading } = useCurrentAdmin();
  const canManage = permLoading ? true : hasPermission(admin, "board.moderate");

  const [st, setSt] = useState(null);
  const [statusFailed, setStatusFailed] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showBackup, setShowBackup] = useState(false);

  useEffect(() => {
    communityAPI.status()
      .then((r) => setSt(r?.data || null))
      .catch(() => setStatusFailed(true));
  }, []);

  const forumOn = !!st?.enabled;

  return (
    <AdminLayout pageTitle="Community"
      pageSubtitle={forumOn ? "The member forum, live from Discourse." : "Forum connection, and the built-in message board."}
      permission="board.view">
      {!st && !statusFailed ? (
        <div className="arb__loading"><div className="acr__spinner"/></div>
      ) : (
        <>
          {st && <CommunityStatus st={st} canManage={canManage} onSynced={() => setRefreshKey((k) => k + 1)} />}

          {forumOn ? (
            <>
              <ForumOverview refreshKey={refreshKey} />
              <RetreatSpaces canManage={canManage} instantSync={st.instantSync} refreshKey={refreshKey} onOpened={() => setRefreshKey((k) => k + 1)} />
              <div className={`acard abd__backup ${showBackup ? "is-open" : ""}`}>
                <button type="button" className="abd__backup-toggle" aria-expanded={showBackup}
                  onClick={() => setShowBackup((v) => !v)}>
                  <HiOutlineArchiveBox className="abd__backup-icon" />
                  <span>
                    <b>Backup board <em>hidden from members</em></b>
                    <small>The old built-in board, from before the forum. Members are sent to the forum instead. Old posts stay here in case you need them.</small>
                  </span>
                  <HiOutlineChevronDown className="abd__backup-chev" />
                </button>
                {showBackup && <div className="abd__backup-body"><LegacyBoard canManage={canManage} /></div>}
              </div>
            </>
          ) : (
            <LegacyBoard canManage={canManage} />
          )}
        </>
      )}
    </AdminLayout>
  );
}
